import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getNavItems } from 'selectors/navigationSelectors'
import useLocalizedNavItems from 'hooks/useLocalizedNavItems'
import { rem } from 'polished'
import { accountAccessShowLogin } from 'actions/UiActions'
import TextButton from 'LuxKit/Button/TextButton'
import NavigationPhoneMenu from './NavigationPhoneMenu'
import NavigationRegionMenu from './NavigationRegionMenu'
import { hasAccountNotification, selectLoggedIn } from 'selectors/accountSelectors'
import AvatarCircle from 'components/Common/AvatarCircle/AvatarCircle'
import FormatCurrency from 'components/Common/FormatCurrency'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getThemeClassName } from 'lib/theme/themeUtils'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import Divider from 'LuxKit/Divider'
import NavigationContainer from './NavigationContainer'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import formatStaticAppText from 'lib/format/formatStaticAppText'

const LoginBlock = styled.div`
  > * + * {
    margin-left: ${rem(12)};
  }
`

const StyledTextButton = styled(TextButton)`
  width: 100%;
`

interface Props {
  navItems: Array<App.NavItem>
  isLoggedIn?: boolean
  hasNotification: boolean
  firstName?: string
  lastName?: string
  balance: number
  canRedeemLuxPlusBenefits: boolean
}

function NavigationMenu(props: Props) {
  const {
    navItems: rawNavItems,
    hasNotification,
    isLoggedIn,
    firstName,
    lastName,
    balance,
    canRedeemLuxPlusBenefits,
  } = props

  const navItems = useLocalizedNavItems(rawNavItems)
  const whiteLabelAppConfig = useWhiteLabelAppConfig()
  const intl = useIntl()

  const dispatch = useAppDispatch()
  const modalElement = useModalElementContext<App.NavItem | undefined>()

  const showLogin = useCallback(() => {
    dispatch(accountAccessShowLogin())
    modalElement.resolve(undefined)
  }, [dispatch, modalElement])

  const onModalClose = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const primaryNavItems = useMemo(() => navItems.filter(navItem => navItem.component !== 'secondary'), [navItems])
  const secondaryNavItems = useMemo(() => navItems.filter(navItem => navItem.component == 'secondary'), [navItems])

  const accountTitle = useMemo(() => {
    if (canRedeemLuxPlusBenefits) {
      return formatStaticAppText(intl, LUXURY_PLUS.PROGRAM_NAME)
    }
    return intl.formatMessage({
      id: 'jhsnhfBo7FCUqyPSQN_A',
      defaultMessage: 'Account',
      description: 'Account menu item title in navigation menu in account settings',
    })
  }, [canRedeemLuxPlusBenefits, intl])

  const accountAriaLabel = useMemo(() => {
    return intl.formatMessage({
      id: '__WxmTd5deXFTDmYiXgV',
      defaultMessage: 'Account',
      description: 'Account menu item aria-label in the navigation menu',
    })
  }, [intl])

  const signUpOrLoginText = useMemo(() => {
    return intl.formatMessage({
      id: 'aMpmQiS5cqB253QNGANK',
      defaultMessage: 'Sign up or login',
      description: 'Sign up or login button text in the navigation menu',
    })
  }, [intl])

  const versionText = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'FsTPeaYOTFl08pnnk1JP',
        defaultMessage: 'v{version}',
        description: 'App version number display in the navigation menu in the site header',
      },
      { version: whiteLabelAppConfig.clientVersion },
    )
  }, [intl, whiteLabelAppConfig.clientVersion])

  return <Modal title="" mode="menu" height="full" className={getThemeClassName({ mobile: 'inverse' })}>
    <Group direction="vertical" gap={20} verticalAlign="space-between" fullHeight>
      <Group direction="vertical" gap={8} data-testid="NavigationMenu">
        <NavigationContainer navItems={primaryNavItems} />
        {!!secondaryNavItems.length && <>
          <Divider kind="primary" />
          <NavigationContainer navItems={secondaryNavItems} />
        </>}
        <Divider kind="primary" />
        <NavigationPhoneMenu onClick={onModalClose} />
        <Divider kind="primary" />
        <NavigationRegionMenu />
        {isLoggedIn && (
          <ClickableListItem
            onClick={() => modalElement.resolve(undefined)}
            to="/account"
            title={accountTitle}
            subtitle={balance > 0 ? <FormatCurrency value={balance} format="dynamicDollar" /> : undefined}
            aria-label={accountAriaLabel}
            titleColour={canRedeemLuxPlusBenefits ? 'lux-plus' : undefined}
            startIcon={
              <ProductPaletteProvider palette={canRedeemLuxPlusBenefits ? 'lux-plus' : 'default'}>
                <AvatarCircle
                  firstName={firstName}
                  lastName={lastName}
                  size="small"
                  variant="dark"
                  nonInteractable
                  hasNotification={hasNotification}
                />
              </ProductPaletteProvider>
            }
          />
        )}
        {!isLoggedIn && (
          <LoginBlock>
            <StyledTextButton
              kind="secondary"
              size="large"
              onClick={showLogin}
              role="menuitem"
            >
              {signUpOrLoginText}
            </StyledTextButton>
          </LoginBlock>
        )}
      </Group>

      {whiteLabelAppConfig.isApp && (
        <Group direction="horizontal" horizontalAlign="end">
          <Caption variant="large">{versionText}</Caption>
        </Group>
      )}
    </Group>
  </Modal>
}

function mapStateToProps(state: App.State) {
  return {
    navItems: getNavItems(state),
    isLoggedIn: selectLoggedIn(state),
    hasNotification: hasAccountNotification(state),
    firstName: state.auth.account.givenName,
    lastName: state.auth.account.surname,
    balance: state.auth.account.balance,
    canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(state),
  }
}

export default connect(mapStateToProps)(NavigationMenu)
